import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { useDispatch, useStore } from 'react-redux';
import merge from 'lodash.merge';

import { mergeStyleWithState } from '../../utils/mergeStyle';
import { history } from '../../configureStore';
import ContainerSkeleton from '../skeleton/ContainerSkeleton';
import { selectVariableData, selectLocalVariableData } from '../../store/page/selectors';
import { combineClassNames, debounce, replacePlaceholdersWithVariablesV2,
  handleOpenUrl, getSizesBasedOnScreen, runFunctionsWithTiming, getNestedValue,
  isClickInsidePortal,
} from '../../utils/base';
import { buildInternalFns } from '../../utils/handlerScope';
import RadioChecked from '../../../assets/icons/components/radio-checked.svg';
import RadioUnchecked from '../../../assets/icons/components/radio-unchecked.svg';
import {
  pageRequest, submitFormRequest, mergeStyleRequest, formValuesRequest, updateVariableSuccess, uncheckDependentRadioRequest, actionRequest,
 } from '../../store/page/actions';
import { buildVariablesData, isVariablesChunk } from '../../utils/variableChunk';
import { StyleUpdateChunk, RequestUpdateChunk, FunctionUpdateChunk, Breakpoints, ActionChunk , Radio as RadioType } from '../../types/base';
import generateComponentStyles from '../../utils/styleHelper';
import { useInitEntrance } from '../../utils/useInitEntrance';

const StyledRadioLabel = styled.label<{
  $shape?: any;
  $baseScreenResolution?: number;
  $skipStates?: boolean;
  $parentDisplay?: string;
  $parentFlexDirection?: string;
}>`
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  z-index: 0;
  ${(props) => generateComponentStyles(props.$shape, props.$baseScreenResolution, props.$skipStates, props.$parentDisplay, undefined, props.$parentFlexDirection)}
`;

const StyledRadio = styled.input`
  z-index: 1;
  cursor: pointer;
  margin: 0;
  padding: 0;
  position: absolute;
  opacity: 0;
  height: 0;
  width: 0;
`;


interface RadioTypeProps extends RadioType {
  dataLake?: any;
  nestedTable?: boolean;
  parentIndex?: number;
  styleChunks?: any;
  parentLoading?: boolean;
  parentStyle?: any;
  enableLocalShimmer?: boolean;
  valueOriginal?: string;
  cache?: boolean;
}

const Radio: React.FC<RadioTypeProps & { pageSettings: any }> = ({ id, style: stDraft, name, events: eventsDraft, soc, value: valueDraft, pageSettings, dataLake, nestedTable, parentIndex, styleChunks, settings, skeleton: renderSkeleton2, parentLoading, parentStyle,
  active_state: activeStateDraft, enableLocalShimmer, valueOriginal, cache,
 }) => {
  const { ref } = settings || {};
  const launchExecutedRef = useRef<undefined | null | string>(null);
  const containerRef = useRef<HTMLLabelElement>(null);
  const { style: styleDraft, events, active_state, value, ref: realRef, name: parsedName } = dataLake?.data ? replacePlaceholdersWithVariablesV2({ ref, value: valueDraft, style: stDraft, events: eventsDraft, active_state: activeStateDraft, name }, dataLake.data) : { ref: undefined, value: valueDraft, style: stDraft, events: eventsDraft, active_state: activeStateDraft, name };
  const mergeStylesWithChunks = useCallback((style: any, chunks: any, index: number) => {
  const { indexes, refs } = chunks || {};
  let mergedStyle = style;
  
  // Apply index-based styles first
  if (indexes && indexes[id] && indexes[id][index] && typeof index === 'number') {
    mergedStyle = merge({}, mergedStyle, indexes[id][index]);
  }
  
  // Apply ref-based updates at the end
  if (refs && realRef) {
    const refUpdate = refs[realRef];
    if (refUpdate?.type && refUpdate.type === 'radio') {
      mergedStyle = merge({}, mergedStyle, refUpdate?.update);
    }
  }
  
  return mergedStyle;
}, [id, realRef]);
  const renderSceleton = useMemo(() => {
    if (enableLocalShimmer) {
      if (cache === false) {
        return false;
      }
      return (active_state === 'loading' || parentLoading) ? !!((cache || (typeof valueOriginal === 'string' && /{{.*}}/.test(valueOriginal)))) : false;
    }
    return renderSkeleton2;
  }, [renderSkeleton2, active_state, enableLocalShimmer, valueOriginal, parentLoading]);

  const store = useStore();
  // First useMemo to merge active state styles with base style
  const { effectiveState, entranceAnimation } = useInitEntrance(styleDraft, active_state, containerRef);
  const mergedStyleWithState = useMemo(() => {
    let mergedStyle = styleDraft;
    if (effectiveState && styleDraft?.states?.[effectiveState]) {
      mergedStyle = mergeStyleWithState({}, styleDraft, styleDraft.states[effectiveState]);
    }

    // Handle states in breakpoints
    if (styleDraft?.breakpoints) {
      mergedStyle.breakpoints = Object.entries(styleDraft.breakpoints).reduce((acc, [breakpoint, breakpointStyle]) => {
        const typedBreakpointStyle = breakpointStyle as Breakpoints;
        if (typedBreakpointStyle?.states?.[effectiveState]) {
          acc[breakpoint] = mergeStyleWithState({}, typedBreakpointStyle, typedBreakpointStyle.states[effectiveState]);
        } else {
          acc[breakpoint] = typedBreakpointStyle;
        }
        return acc;
      }, {} as typeof styleDraft.breakpoints);
    }

    if (entranceAnimation) {
      mergedStyle = { ...mergedStyle, animation: entranceAnimation };
    }
    return mergedStyle;
  }, [styleDraft, effectiveState, entranceAnimation]);

  // Second useMemo to merge styles with chunks based on parentIndex
  const style = useMemo(() => mergeStylesWithChunks(mergedStyleWithState, styleChunks, parentIndex), [mergedStyleWithState, styleChunks, parentIndex]);
  const baseScreenResolution =  pageSettings?.baseScreenResolution ? getSizesBasedOnScreen(pageSettings?.baseScreenResolution) : undefined;
  const [checkedInternal, setCheckedInternal] = useState<'true'| 'false' | boolean>(value === 'true' ? true : value === 'false' ? false : value);
  const debouncedSubmitForm = useCallback(
    debounce(() => {
      dispatch(submitFormRequest({ value: 'soc', componentId: id }));
    }, 500),
    []
  );

  useEffect(() => {
    setCheckedInternal(value === 'true' ? true : value === 'false' ? false : value);
  }, [value]);

  const {
    on_change: clickUpdates,
    on_launch: launchUpdates,
    on_hover: hoverUpdates,
    on_unhover: unhoverUpdates,
    on_outclick: outclickUpdates,
    on_success: onSuccessUpdates,
    on_error: onErrorUpdates,
  } = events || {};
  const dispatch = useDispatch();
  const timeoutsRef = useRef<NodeJS.Timeout[]>([]);

  // TNBEWEB-1992: shared handler scope (adds data.* / _c.* / _config.* / this.* on top of the
  // legacy $get/$set); `thisRow`/`rowPath` come from the repeatable row context (undefined otherwise).
  const internalFns = buildInternalFns({
    store,
    thisRow: dataLake?.data,
    rowPath: dataLake?.rowPath,
    owner: { id, parentIndex, row: dataLake?.data, activeState: active_state },
  });

  // Variables are assigned before every other chunk in the event (first in the
  // stack). Resolve them synchronously so the style/action/function chunks that
  // follow already see the new values. `{{this.*}}` resolves against this
  // component's own data.
  const applyVariablesFirst = (updates: any[]) => {
    const variableUpdates = (updates || []).filter((u) => isVariablesChunk(u));
    if (!variableUpdates.length) return;
    const state: any = store.getState();
    const data = buildVariablesData(variableUpdates as any, {
      variables: selectVariableData(state),
      localVariables: selectLocalVariableData(state),
      thisContext: dataLake?.data,
      formValues: state.page?.formValues,
    });
    if (data && Object.keys(data).length) {
      dispatch(updateVariableSuccess({ data }));
    }
  };

  const handleActionChunk = (actionUpdates: ActionChunk[], filterLoadingState?: StyleUpdateChunk[], onSuccessEvts?: any[], onErrorEvts?: any[]) => {
    actionUpdates.forEach((update) => {
      dispatch(actionRequest({ 
        actionUpdate: update, 
        componentId: id,
        onSuccess: onSuccessEvts,
        onError: onErrorEvts,
      }, filterLoadingState));
    });
  };

  const handleCheckboxChange = () => {
    if (checkedInternal && checkedInternal !== 'false') return;
    setCheckedInternal(!checkedInternal);
    dispatch(formValuesRequest({ field: { id: realRef || id, value: !checkedInternal } }));
    dispatch(uncheckDependentRadioRequest({ excludeId: realRef || id, name: parsedName, originalName: name }));
    if (soc) debouncedSubmitForm(!checkedInternal);
  };

  const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (active_state === 'disabled' || active_state === 'loading') {
      e.preventDefault();
      e.stopPropagation();
      return;
    }
    handleCheckboxChange();
    e.preventDefault();
    if (clickUpdates) {
      e.stopPropagation();
      const styleUpdates = clickUpdates.filter((update): update is StyleUpdateChunk => update && 'style' in update);
      const requestUpdates = clickUpdates.filter((update): update is RequestUpdateChunk => update && 'request' in update);
      const functionUpdates = clickUpdates.filter((update): update is FunctionUpdateChunk => update && 'function' in update);
      const actionUpdates = clickUpdates.filter((update): update is ActionChunk => update && 'action' in update);
      applyVariablesFirst(clickUpdates);
      const runEffects = () => {
        if (styleUpdates.length) {
          dispatch(mergeStyleRequest(styleUpdates, parentIndex, dataLake?.data));
        }
        if (actionUpdates.length) {
          const filterLoadingState = styleUpdates?.filter((update) => update?.active_state === 'loading');
          handleActionChunk(actionUpdates, filterLoadingState, onSuccessUpdates, onErrorUpdates);
        }
        if (requestUpdates.length) {
          requestUpdates.forEach((update) => {
            const { method, value, silent, config } = update?.request || {};
            const filterLoadingState = styleUpdates?.filter((update) => update?.active_state === 'loading');
            const { delay } = config || {};
            if (update?.request && method === 'command') {
              dispatch(pageRequest({
                location: { pathname: value },
                silent,
                componentId: id,
                onSuccess: onSuccessUpdates,
                onError: onErrorUpdates,
               },
              filterLoadingState
              ));
            } else if (update?.request && method === 'action') {
              dispatch(submitFormRequest({ value, silent, delay, componentId: id, onSuccess: onSuccessUpdates, onError: onErrorUpdates }, filterLoadingState));
            } else if (update?.request && method === 'open-url') {
              handleOpenUrl(value, delay, silent, history);
            }
          });
        }
      };
      runFunctionsWithTiming(functionUpdates, internalFns, runEffects, e);
    }
  }

  const handleHover = (e: React.PointerEvent<HTMLDivElement>) => {
    if (e.pointerType === 'touch') return; // TNBEWEB-1980: finger tap — don't apply hover
    if (hoverUpdates) {
      const styleUpdates = hoverUpdates.filter((update): update is StyleUpdateChunk => update && 'style' in update);
      const requestUpdates = hoverUpdates.filter((update): update is RequestUpdateChunk => update && 'request' in update);
      const functionUpdates = hoverUpdates.filter((update): update is FunctionUpdateChunk => update && 'function' in update);
      const actionUpdates = hoverUpdates.filter((update): update is ActionChunk => update && 'action' in update);
      applyVariablesFirst(hoverUpdates);
      const runEffects = () => {
        if (styleUpdates.length) {
          dispatch(mergeStyleRequest(styleUpdates, parentIndex, dataLake?.data));
        }
        if (actionUpdates.length && (active_state !== 'disabled' && active_state !== 'loading')) {
          handleActionChunk(actionUpdates, undefined, onSuccessUpdates, onErrorUpdates);
        }
        if (requestUpdates.length && (active_state !== 'disabled' && active_state !== 'loading')) {
          requestUpdates.forEach((update) => {
            const { method, value, silent, config } = update?.request || {};
            const { delay } = config || {};
            if (update?.request && method === 'action') {
              dispatch(submitFormRequest({ value, silent, delay, componentId: id, onSuccess: onSuccessUpdates, onError: onErrorUpdates }));
            } else if (update?.request && method === 'open-url') {
              handleOpenUrl(value, delay, silent, history);
            }
          });
        }
      };
      runFunctionsWithTiming(functionUpdates, internalFns, runEffects, e);
    }
  }

  const handleUnhover = (e: React.PointerEvent<HTMLDivElement>) => {
    if (e.pointerType === 'touch') return; // TNBEWEB-1980: finger tap — don't apply unhover
    if (unhoverUpdates) {
      const styleUpdates = unhoverUpdates.filter((update): update is StyleUpdateChunk => update && 'style' in update);
      const requestUpdates = unhoverUpdates.filter((update): update is RequestUpdateChunk => update && 'request' in update);
      const functionUpdates = unhoverUpdates.filter((update): update is FunctionUpdateChunk => update && 'function' in update);
      const actionUpdates = unhoverUpdates.filter((update): update is ActionChunk => update && 'action' in update);
      applyVariablesFirst(unhoverUpdates);
      const runEffects = () => {
        if (styleUpdates.length) {
          dispatch(mergeStyleRequest(styleUpdates, parentIndex, dataLake?.data));
        }
        if (actionUpdates.length && (active_state !== 'disabled' && active_state !== 'loading')) {
          handleActionChunk(actionUpdates, undefined, onSuccessUpdates, onErrorUpdates);
        }
        if (requestUpdates.length && (active_state !== 'disabled' && active_state !== 'loading')) {
          requestUpdates.forEach((update) => {
            const { method, value, silent, config } = update?.request || {};
            const { delay } = config || {};
            if (update?.request && method === 'action') {
              dispatch(submitFormRequest({ value, silent, delay, componentId: id, onSuccess: onSuccessUpdates, onError: onErrorUpdates }));
            } else if (update?.request && method === 'open-url') {
              handleOpenUrl(value, delay, silent, history);
            }
          });
        }
      };
      runFunctionsWithTiming(functionUpdates, internalFns, runEffects, e);
    }
  }

  useEffect(() => {
    if (launchExecutedRef.current !== style?.visibility && launchUpdates && launchUpdates.length) {
      launchExecutedRef.current = style?.visibility; // Set the flag to true
      const styleUpdates = launchUpdates.filter((update): update is StyleUpdateChunk => update && 'style' in update);
      const requestUpdates = launchUpdates.filter((update): update is RequestUpdateChunk => update && 'request' in update);
      const functionUpdates = launchUpdates.filter((update): update is FunctionUpdateChunk => update && 'function' in update);
      const actionUpdates = launchUpdates.filter((update): update is ActionChunk => update && 'action' in update);
      applyVariablesFirst(launchUpdates);
      const runEffects = () => {
        if (styleUpdates.length) {
          dispatch(mergeStyleRequest(styleUpdates, parentIndex, dataLake?.data));
        }
        if (actionUpdates.length) {
          handleActionChunk(actionUpdates, undefined, onSuccessUpdates, onErrorUpdates);
        }
        if (requestUpdates.length) {
          requestUpdates.forEach((update) => {
            const { method, value, silent, config } = update?.request || {};
            const { delay } = config || {};
            if (update?.request && method === 'action') {
              // const newTimeout = setTimeout(() => {
              //   dispatch(submitFormRequest({ value, silent, delay, componentId: id }));
              // }, config?.delay * 1000 || 0);

              // timeoutsRef.current.push(newTimeout);
            } else if (update?.request && method === 'open-url') {
              handleOpenUrl(value, delay, silent, history);
            }
          });
        }
      };
      runFunctionsWithTiming(functionUpdates, internalFns, runEffects);
    }

    return () => {
      timeoutsRef?.current?.forEach((timeout) => {
        clearTimeout(timeout);
      });
    };
  }, [launchUpdates, style?.visibility]);

  // Memoize the filtered updates to avoid recalculating on every click
  const filteredUpdates = useMemo(() => {
    if (!outclickUpdates) return null;
    
    return {
      styleUpdates: outclickUpdates.filter((update): update is StyleUpdateChunk => update && 'style' in update),
      requestUpdates: outclickUpdates.filter((update): update is RequestUpdateChunk => update && 'request' in update),
      functionUpdates: outclickUpdates.filter((update): update is FunctionUpdateChunk => update && 'function' in update),
      actionUpdates: outclickUpdates.filter((update): update is ActionChunk => update && 'action' in update)
    };
  }, [outclickUpdates]);

  useEffect(() => {
    if (!outclickUpdates?.length) return undefined;
    if (style?.visibility === 'hidden') return undefined;

    const handleUnclick = (e: MouseEvent) => {
      if (active_state === 'disabled' || active_state === 'loading') return;
      
      if (
        outclickUpdates?.length &&
        containerRef.current && 
        !containerRef.current.contains(e.target as Node) &&
        !isClickInsidePortal(e.target as Node) &&
        filteredUpdates
      ) {
        const runEffects = () => {
          // Apply style updates
          if (filteredUpdates.styleUpdates.length) {
            dispatch(mergeStyleRequest(filteredUpdates.styleUpdates, parentIndex));
          }

          // Handle action updates
          if (filteredUpdates.actionUpdates.length) {
            handleActionChunk(filteredUpdates.actionUpdates, undefined, onSuccessUpdates, onErrorUpdates);
          }

          // Process request updates
          if (filteredUpdates.requestUpdates.length) {
            filteredUpdates.requestUpdates.forEach((update) => {
              const { method, value, silent, config } = update?.request || {};
              const { delay } = config || {};
              if (update?.request && method === 'action') {
                dispatch(submitFormRequest({
                  value,
                  silent,
                  delay,
                  componentId: id,
                  onSuccess: onSuccessUpdates,
                  onError: onErrorUpdates,
                }));
              } else if (update?.request && method === 'open-url') {
                handleOpenUrl(value, delay, silent, history);
              }
            });
          }
        };
        applyVariablesFirst(outclickUpdates);
        runFunctionsWithTiming(filteredUpdates.functionUpdates, internalFns, runEffects);
      }
    };

    document.addEventListener('mousedown', handleUnclick, true);
    return () => {
      document.removeEventListener('mousedown', handleUnclick, true);
    };
  }, [
    parentIndex, 
    active_state, 
    id, 
    stDraft?.states?.loading,
    outclickUpdates?.length,
    style?.visibility,
  ]);


  if (renderSceleton && style) {
    return <ContainerSkeleton
      style={style}
      baseScreenResolution={baseScreenResolution}
      skipStates={active_state === 'disabled' || active_state === 'loading'}
    />;
  }

  // Mouse handlers are typed for the div/input event the shared executeFunction
  // expects; the label element they're bound to differs, so keep the element
  // alias untyped (matches the previously-spread motionAnimProps: any).
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const LabelEl: any = StyledRadioLabel;

  return (
    <LabelEl
      ref={containerRef}
      htmlFor={`${realRef || id}${(nestedTable && typeof parentIndex === 'number' && !realRef) ? `::${parentIndex}` : ''}`}
      $shape={style}
      $baseScreenResolution={baseScreenResolution}
      $skipStates={active_state === 'disabled' || active_state === 'loading'}
      $parentDisplay={parentStyle?.display}
      $parentFlexDirection={typeof parentStyle?.flex === 'object' ? parentStyle?.flex?.direction : undefined}
      data-component="radio"
      className={combineClassNames(style.class)}
      onClick={handleClick}
      onPointerEnter={hoverUpdates?.length ? handleHover : undefined}
      onPointerLeave={unhoverUpdates?.length ? handleUnhover : undefined}
    >
      <StyledRadio
        data-id={realRef || id}
        id={`${realRef || id}${(nestedTable && typeof parentIndex === 'number' && !realRef) ? `::${parentIndex}` : ''}`}
        name={parsedName}
        value={id}
        type="radio"
        checked={checkedInternal}
      />
      {checkedInternal ? (settings?.icons?.active ? <img style={{ maxWidth: '100%', maxHeight: '100%' }} src={settings.icons.active} /> : <RadioChecked />) : (
        settings?.icons?.inactive ? <img style={{ maxWidth: '100%', maxHeight: '100%' }} src={settings.icons.inactive} /> : <RadioUnchecked />
      )}
    </LabelEl>
  );
}

export default Radio;
